<template>
  <div class="onboarding-signup-step-category">
    <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
      <QPage padding>
        <h2 class="step-title">
          {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE", { type: selectedFamilyType })) }}
        </h2>

        <Category :selected-family-categories="selectedFamilyCategories" :selected-category-id="selectedCategoryId" @on-select="selectCategory" />
      </QPage>
    </transition>
  </div>
</template>

<script setup>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { GETTER_FAMILY, ACTION_FAMILY_INIT, ACTION_SIGNUP_UPDATE, GETTER_SIGNUP_GET } from "@etab/stores/types";
  import { useSignupStore } from "@etab/stores/signup";
  import { useEtabFamilyStore } from "@etab/stores/etab-family";
  import { IEtabFamily, EtabFamilyModel, ISignup } from "@core/models";
  import { FUNNEL_SIGNUP_MAIN } from "@etab/config";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import { PosthogManager } from "@etab/managers/posthog.manager";
  import { FIREBASE_EVENTS } from "@core/constants";
  import { computed, onMounted } from "vue";
  import { useSignup } from "@etab/composables";
  import { useI18n } from "vue-i18n";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useTransition } from "@core/composables";
  import Category from "@etab/components/onboarding/signup/Category.component.vue";

  const { transition } = useTransition();
  const { $_nextStep } = useSignup();
  const { t } = useI18n();
  const signupStore = useSignupStore();
  const etabFamilyStore = useEtabFamilyStore();

  const families = computed(() => {
    return etabFamilyStore[GETTER_FAMILY];
  });

  const selectedFamilyId = computed(() => {
    return signupStore[GETTER_SIGNUP_GET][ISignup.familyId];
  });

  const selectedFamily = computed(() => {
    return new EtabFamilyModel(families.value.find((family) => family[IEtabFamily.id] === selectedFamilyId.value));
  });

  const selectedFamilyType = computed(() => {
    switch (selectedFamily.value[IEtabFamily.code]) {
      case "F_AUTRE": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_OTHER");
      }
      case "A_EMPORTER": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_OTHER");
      }
      case "COLLECTIVE": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_OTHER");
      }
      case "MAGASIN": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_RETAIL");
      }
      case "SANTE": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_SANTE");
      }
      case "HOTEL": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_HOSTEL");
      }
      case "CAFE_REST": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_CAFE_RESTAURANT");
      }
      case "BAR_CLUB": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_BAR_CLUB");
      }
      case "EVENEMENT": {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_EVENT");
      }
      default: {
        return t("ETAB.PAGES.SIGNUP.STEPS.MAIN.CATEGORY.@TITLE_TYPE", {
          type: (selectedFamily.value[IEtabFamily.name] || "").toLowerCase(),
        });
      }
    }
  });

  const selectedFamilyCategories = computed(() => {
    return selectedFamily.value.getCategories();
  });

  const selectedCategoryId = computed(() => {
    return signupStore[GETTER_SIGNUP_GET][ISignup.categoryId];
  });

  const shouldDisplayEmployeeStep = computed(() => {
    return (
      signupStore[GETTER_SIGNUP_GET].isCHR() ||
      signupStore[GETTER_SIGNUP_GET].isLargeAccount() ||
      signupStore[GETTER_SIGNUP_GET].isRetail() ||
      signupStore[GETTER_SIGNUP_GET].isHotel()
    );
  });

  const initFamilies = async function () {
    try {
      LoadingManager.show();
      await etabFamilyStore[ACTION_FAMILY_INIT]();
    } catch (error) {
      NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const selectCategory = function (category) {
    TrackingManager.addEvent({
      event: FIREBASE_EVENTS.FUNNEL,
      step: "step7_category",
    });
    const data = {
      [ISignup.categoryId]: category.getId(),
      [ISignup.segment]: category.getSegment(),
    };
    signupStore[ACTION_SIGNUP_UPDATE](data);
    PosthogManager.captureEvent("sign_up", { $step: FUNNEL_SIGNUP_MAIN.category, $is_optionnal: false, $step_position: 4 });
    goToNextStep();
  };

  const goToNextStep = function () {
    if (!signupStore[GETTER_SIGNUP_GET].getSegment() || signupStore[GETTER_SIGNUP_GET].isHotel()) {
      // RG: Etab Creation Category - if no segment we ask more questions in order to define segment
      $_nextStep({ step: { name: FUNNEL_SIGNUP_MAIN.check } });
    } else if (shouldDisplayEmployeeStep.value) {
      // RG: Etab Creation Category - if segment is CHR or Retail we ask for size
      $_nextStep({ step: { name: FUNNEL_SIGNUP_MAIN.employees } });
    } else {
      // otherwise we skip the questions step to go to identity
      $_nextStep({ step: { name: FUNNEL_SIGNUP_MAIN.identity } });
    }
  };

  onMounted(() => {
    if (families.value.length === 0) {
      initFamilies();
    }
  });
</script>
